import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { FormSelect } from '~/components/ui/form-items/select'
import { useMutation } from '~/hooks/use-mutation'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { useToast } from '~/hooks/use-toast'
import { userRoleSchema, type UserRoleFormData } from '~/schemas/user-role'
import { useAppStore } from '~/stores/app'
import { useSessionStore } from '~/stores/session'
import type { AdminUser } from '~/types/admin-user'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import { Role } from '~/types/user'

type UserRoleDialogProps = {
  targetUser: AdminUser | null
}

export function UserRoleDialog({ targetUser }: UserRoleDialogProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)
  const currentUser = useSessionStore((state) => state.user)

  const isOpen = openDialogIds.includes(DialogId.UPDATE_USER_ROLE)
  const isSelfTarget = !!(targetUser && currentUser && targetUser.id === currentUser.id)

  const { control, handleSubmit, reset } = useForm<UserRoleFormData>({
    resolver: zodResolver(userRoleSchema),
    defaultValues: {
      role: Role.USER,
    },
  })

  useEffect(() => {
    if (targetUser && isOpen) {
      reset({ role: targetUser.role })
    }
  }, [targetUser, isOpen, reset])

  const { mutate: updateRole, isPending } = useMutation<{ user: AdminUser }, UserRoleFormData>({
    mutationKey: MutationKey.UPDATE_USER_ROLE,
    method: 'PATCH',
    idParams: targetUser ? { id: targetUser.id } : undefined,
    options: {
      onSuccess: () => {
        showSuccess(t('users.roleUpdated'))
        invalidateQueries(QueryKey.ADMIN_USERS)
        if (targetUser) {
          invalidateQueries(QueryKey.ADMIN_USER_DETAIL)
        }
        closeDialog(DialogId.UPDATE_USER_ROLE)
      },
    },
  })

  const onSubmit = (data: UserRoleFormData) => {
    if (isSelfTarget) return
    updateRole(data)
  }

  const roleOptions = [
    { label: t('users.roleAdmin'), value: Role.ADMIN },
    { label: t('users.roleUser'), value: Role.USER },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(DialogId.UPDATE_USER_ROLE)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.changeRoleTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <p className="text-sm text-muted-foreground">{t('users.changeRoleDescription')}</p>
          <FormSelect
            control={control}
            name="role"
            label={t('users.role')}
            options={roleOptions}
            disabled={isSelfTarget}
          />
          {isSelfTarget && (
            <p className="text-sm text-destructive" role="alert">
              {t('users.selfRoleWarning')}
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => closeDialog(DialogId.UPDATE_USER_ROLE)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              id="change-role-confirm-button"
              type="submit"
              disabled={isSelfTarget || isPending}
              loading={isPending}
            >
              {t('common.confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
